import moment from "moment";

const DEFAULT_CONSENT_COLLECTION = "talmidsdb/consents";

function firstValue(item, fields, fallback = "") {
	for (const field of fields) {
		const value = item?.[field];
		if (value !== undefined && value !== null && value !== "") return value;
	}
	return fallback;
}

export function normalizeConsentBoolean(value) {
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["false", "0", "no", "n"].includes(normalized)) return false;
		if (["true", "1", "yes", "y"].includes(normalized)) return true;
	}
	return Boolean(value);
}

export function formatConsentType(value) {
	const raw = String(value || "").trim();
	if (!raw) return "Not specified";
	return raw.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatConsentDate(value) {
	if (value === null || value === undefined || value === "") return "-";
	const numeric = typeof value === "number" ? value : /^\d{10,}$/.test(String(value)) ? Number(value) : null;
	const parsed = numeric !== null ? moment(numeric) : moment(value);
	return parsed.isValid() ? parsed.format("DD/MMM/YYYY HH:mm") : "-";
}

export function getConsentPermissions(item = {}) {
	const safeItem = item && typeof item === "object" ? item : {};
	const permissions = [
		["Photos", firstValue(safeItem, ["photosCheckBox", "photoCheckbox"])],
		["Videos", firstValue(safeItem, ["videosCheckBox", "videoCheckbox"])],
		["Written Stories", firstValue(safeItem, ["writtenStoriesCheckBox", "writtenStoryCheckbox"])],
		["Name in Visual Assets", firstValue(safeItem, ["visualAssetsCheckBox", "shareNameOnVisualAssets"])],
	];
	return permissions.filter(([, value]) => normalizeConsentBoolean(value)).map(([label]) => label);
}

export function normalizeConsentRecord(item = {}) {
	const safeItem = item && typeof item === "object" ? item : {};
	const consentType = firstValue(safeItem, ["consentType", "type"]);
	return {
		...safeItem,
		id: firstValue(safeItem, ["id", "consentRequestID"]),
		consentRequestID: firstValue(safeItem, ["consentRequestID", "id"]),
		consentType,
		consentTypeLabel: formatConsentType(consentType),
		name: firstValue(safeItem, ["consenting", "fullName", "consentTo", "childrenNames", "consentGivenFor", "consentFor"], "Not specified"),
		consentTo: firstValue(safeItem, ["consentTo", "childrenNames", "consenting", "consentGivenFor", "consentFor"], "Not specified"),
		communityID: firstValue(safeItem, ["communityID", "communityId"]),
		organisationID: firstValue(safeItem, ["organisationID", "organizationID", "orgID", "orgId", "organization", "organisation"]),
		countryID: firstValue(safeItem, ["countryID", "countryId"]),
		submittedByID: firstValue(safeItem, [
			"userId", "userID", "submittedBy", "submittedByUserID", "submitedByuserID", "createdBy", "addedByUser", "submitedBy", "submitter"
		]),
		date: firstValue(safeItem, ["consentDate", "timeStamp", "timestamp", "createdAt"]),
		permissions: getConsentPermissions(safeItem),
		communityConsent: normalizeConsentBoolean(firstValue(safeItem, [
			"communityConsentCheckbox", "communityConcentCheckbox", "communityConsent",
		])),
		_sourceCollection: safeItem._sourceCollection || DEFAULT_CONSENT_COLLECTION,
	};
}

export function getConsentEndpoint(item = {}) {
	const safeItem = item && typeof item === "object" ? item : {};
	return String(safeItem._sourceCollection || DEFAULT_CONSENT_COLLECTION)
		.replace(/^\/+/, "")
		.replace(/^talmidsdb\//, "");
}

export function getConsentMediaReferences(item = {}) {
	const safeItem = item && typeof item === "object" ? item : {};
	const mediaFiles = Array.isArray(safeItem.mediaFiles) ? safeItem.mediaFiles :
		safeItem.mediaFiles && typeof safeItem.mediaFiles === "object" ? Object.values(safeItem.mediaFiles) : [];
	return {
		signature: firstValue(safeItem, ["sigConsent", "signatureConsent"]),
		voice: firstValue(safeItem, ["voiceConsent", "voiceRecording"]),
		additional: mediaFiles.filter(Boolean),
	};
}

export function matchesConsentSearch(row = {}, searchText = "") {
	const needle = String(searchText || "").trim().toLowerCase();
	if (!needle) return true;
	return [
		row.consentType, row.name, row.notes, row.community, row.user,
		row.organisation, row.time, row.phone, row.gender,
	].some((value) => String(value || "").toLowerCase().includes(needle));
}
